import { useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    Activity,
    Building2,
    ClipboardList,
    LogOut,
    Moon,
    Send,
    Shield,
    Sun,
    Users as UsersIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";

const NAV = [
    { to: "/admin", icon: Activity, label: "Overview", testid: "nav-admin-overview", end: true },
    { to: "/admin/tenants", icon: Building2, label: "Tenants", testid: "nav-admin-tenants" },
    { to: "/admin/users", icon: UsersIcon, label: "Users", testid: "nav-admin-users" },
    { to: "/admin/outbox", icon: Send, label: "Outbox", testid: "nav-admin-outbox" },
    { to: "/admin/audit-log", icon: ClipboardList, label: "Audit log", testid: "nav-admin-audit" },
];

function deriveTitle(p) {
    if (p === "/admin") return "Overview";
    if (p.startsWith("/admin/tenants/")) return "Tenant detail";
    if (p === "/admin/tenants") return "Tenants";
    if (p === "/admin/users") return "Users";
    if (p === "/admin/outbox") return "Notification outbox";
    if (p === "/admin/audit-log") return "Audit log";
    return "Admin";
}

export function AdminShell({ children }) {
    const { user, logout } = useAuth();
    const { theme, setTheme } = useTheme();
    const loc = useLocation();
    const nav = useNavigate();

    useEffect(() => {
        document.title = `AutoBrix Admin · ${deriveTitle(loc.pathname)}`;
    }, [loc.pathname]);

    return (
        <div className="flex min-h-screen flex-col bg-background">
            <ImpersonationBanner />
            {/* Admin topbar */}
            <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-zinc-200 bg-white/85 px-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/85">
                <div className="flex items-center gap-3">
                    <Link to="/admin" className="flex items-center gap-2" data-testid="admin-brand">
                        <Shield className="h-5 w-5 text-brand-red" />
                        <div className="leading-tight">
                            <div className="font-display text-sm font-extrabold tracking-tight text-zinc-900 dark:text-zinc-50">
                                AutoBrix Admin
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                Platform console · v0.5
                            </div>
                        </div>
                    </Link>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                        aria-label="Toggle theme"
                        data-testid="admin-theme-toggle"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid="admin-user-menu" className="gap-2">
                                <Shield className="h-4 w-4" />
                                <span className="hidden text-sm md:inline">{user?.name || user?.email}</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                            <DropdownMenuLabel className="flex flex-col">
                                <span className="text-sm font-medium">{user?.name}</span>
                                <span className="text-xs text-zinc-500">{user?.email}</span>
                                <span className="mt-1 inline-flex w-fit rounded border border-red-300 bg-red-50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:border-red-500/40 dark:bg-red-500/15 dark:text-red-300">
                                    platform_admin
                                </span>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onSelect={async () => {
                                    await logout();
                                    nav("/login", { replace: true });
                                }}
                                data-testid="admin-menu-signout"
                                className="text-rose-600"
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="flex flex-1">
                {/* Admin sidebar */}
                <aside
                    data-testid="admin-sidebar"
                    className="hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 flex-col gap-1 border-r border-zinc-200 bg-white py-3 md:flex dark:border-zinc-800 dark:bg-zinc-950"
                >
                    <div className="px-3 pb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400">
                        Platform
                    </div>
                    <nav className="flex flex-col gap-0.5 px-2">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                                        isActive
                                            ? "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
                                            : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-50"
                                    )
                                }
                            >
                                <item.icon className="h-4 w-4 shrink-0" />
                                <span className="flex-1">{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>
                    <div className="mt-auto px-4 pb-3">
                        <div className="rounded-md border border-red-200 bg-red-50/60 p-3 text-xs leading-relaxed text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
                            <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-red-600 dark:text-red-300">
                                Sub-phase 8.0b
                            </div>
                            Master control plane. Cross-tenant operations are logged in the platform audit log.
                        </div>
                    </div>
                </aside>

                <main className="flex-1 px-4 py-6 md:px-8">
                    <div className="mx-auto max-w-7xl">{children}</div>
                </main>
            </div>
        </div>
    );
}
